import { Component, inject, Input, OnInit } from '@angular/core'
import { Router, RouterLink } from '@angular/router'
import { JudgeProfile } from '@ftr/annotations-contracts'
import { PeopleProfilesService } from '@ftr/api-annotations'
import { CourtSystemWithMetadata } from '@ftr/contracts/api/court-system'
import { UserLocation, UserWithGlobalAdministratorRole } from '@ftr/contracts/api/user'
import { TRANSCRIBER_PERMISSIONS } from '@ftr/contracts/api/user-group'
import { IdentityProviderName } from '@ftr/contracts/type/account'
import { ProductFeature, VocabularyTerms } from '@ftr/contracts/type/core'
import { DEFAULT_VOCABULARY_TERMS } from '@ftr/contracts/type/core/VocabularyTerms'
import { Uuid } from '@ftr/contracts/type/shared'
import {
  AnimatedRemoteComponent,
  ApiResult,
  CapitalizeFirstLetterPipe,
  Icon,
  IconComponent,
  LoaderComponent,
  mapData,
  RemoteData,
  TypedContextDirective,
} from '@ftr/foundation'
import { AppPaths, CourtSystemPaths } from '@ftr/routing-paths'
import {
  CourtSystemSelectorBadgeTypes,
  CourtSystemSelectorItem,
  CourtSystemSelectorItemComponent,
  mapCourtSystemWithMetadataToCourtSystemSelectorItem,
} from '@ftr/ui-court-system'
import {
  AuthenticationService,
  GetCurrentUserLocationAction,
  MfaStatusComponent,
  UserAvatarComponent,
  UserState,
} from '@ftr/ui-user'
import { VocabularyTermsService } from '@ftr/ui-vocab'
import { Store } from '@ngxs/store'
import { BehaviorSubject, combineLatest, filter, from, map, of, startWith, switchMap } from 'rxjs'
import { GlobalNavItems } from '../../navigation/global-nav-items'
import { PersistentFlagSwitchComponent } from './persistent-flag-switch'

export interface PersonalMenu {
  user: UserWithGlobalAdministratorRole
  courtSystem: CourtSystemSelectorItem | undefined
  courtSystemCount: number
  isMfaEnabled: boolean | undefined
  identityProviderTitle: string
  terms: VocabularyTerms
  identityProviderIcon?: Icon
  locationString?: string
  judgeRouterLink?: string[]
}

@Component({
  selector: 'ftr-personal-menu',
  templateUrl: './personal-menu.component.html',
  styleUrls: ['./personal-menu.component.css'],
  imports: [
    AnimatedRemoteComponent,
    UserAvatarComponent,
    IconComponent,
    RouterLink,
    CourtSystemSelectorItemComponent,
    LoaderComponent,
    TypedContextDirective,
    MfaStatusComponent,
    CapitalizeFirstLetterPipe,
    PersistentFlagSwitchComponent,
  ],
})
export class PersonalMenuComponent implements OnInit {
  private readonly peopleProfilesService = inject(PeopleProfilesService)

  @Input() currentCourtSystemId: Uuid | undefined
  @Input() ftrLogoPath: string

  viewModel$: ApiResult<PersonalMenu>
  refresh = new BehaviorSubject<void>(undefined)

  protected readonly skipBadges: CourtSystemSelectorBadgeTypes[] = ['isPublished']

  constructor(
    protected readonly globalNavItems: GlobalNavItems,
    private readonly store: Store,
    private readonly authenticationService: AuthenticationService,
    private readonly router: Router,
    private readonly vocabularyTermsService: VocabularyTermsService,
  ) {}

  ngOnInit(): void {
    this.viewModel$ = this.loadViewModel()
  }

  retry(): void {
    this.refresh.next()
  }

  onCourtSystemSelect(event: CourtSystemSelectorItem): void {
    const url = `${AppPaths.CourtSystem}/${event.id}`
    void this.router.navigateByUrl(url)
  }

  private loadViewModel(): ApiResult<PersonalMenu> {
    return this.refresh.pipe(
      switchMap(() =>
        combineLatest([
          this.store.select(UserState.currentCourtSystem),
          this.store.select(UserState.hasStaticProductFeature(ProductFeature.JudgeAnnotationsLibrary)),
        ]),
      ),
      switchMap(([courtSystem, judgeAnnotationsEnabled]) => {
        const userJudge =
          courtSystem && judgeAnnotationsEnabled
            ? this.peopleProfilesService.getPeopleProfileForUser(courtSystem.id, 'Judge')
            : ApiResult.success<JudgeProfile | null>(null)

        return ApiResult.combine([
          this.store.select(UserState.user).pipe(filter(remoteData => remoteData.isSuccess())),
          ApiResult.from(
            this.store
              .dispatch(new GetCurrentUserLocationAction())
              .pipe(switchMap(() => this.store.select(UserState.currentUserLocation))),
          ),
          ApiResult.success(courtSystem),
          this.store.select(UserState.courtSystems),
          ApiResult.from(from(this.authenticationService.multifactorAuthenticationEnabled)),
          ApiResult.from(
            this.store
              .select(UserState.hasOnlyPermissionsInAllCourtSystems)
              .pipe(map(fn => fn(...TRANSCRIBER_PERMISSIONS))),
          ),
          ApiResult.from(
            this.currentCourtSystemId
              ? this.vocabularyTermsService.observeTerms(this.currentCourtSystemId)
              : of(DEFAULT_VOCABULARY_TERMS),
          ),
          userJudge,
        ]).pipe(
          startWith(RemoteData.loading()),
          mapData(
            ([
              user,
              userLocation,
              currentCourtSystem,
              courtSystems,
              isMfaEnabled,
              hasOnlyTranscriberPermissions,
              terms,
              judgeProfile,
            ]) => {
              const identityProvider = this.authenticationService.identityProvider ?? IdentityProviderName.Cognito
              return {
                user,
                courtSystem: this.currentCourtSystemId
                  ? mapCourtSystemWithMetadataToCourtSystemSelectorItem(
                      courtSystems.find(c => c.id === this.currentCourtSystemId) as CourtSystemWithMetadata,
                    )
                  : currentCourtSystem,
                courtSystemCount: courtSystems?.length > 0 && !hasOnlyTranscriberPermissions ? courtSystems.length : 0,
                isMfaEnabled,
                identityProviderTitle: identityProvider === IdentityProviderName.Cognito ? 'FTR' : identityProvider,
                identityProviderIcon:
                  identityProvider === IdentityProviderName.Cognito ? undefined : (identityProvider as Icon),
                locationString: getLocationString(userLocation),
                terms,
                judgeRouterLink:
                  judgeProfile && currentCourtSystem
                    ? ['/', AppPaths.CourtSystem, currentCourtSystem.id, CourtSystemPaths.Judges, judgeProfile.id]
                    : undefined,
              }
            },
          ),
        )
      }),
    )
  }
}

function getLocationString(userLocation: UserLocation | undefined): string {
  if (!userLocation || userLocation.locationString === '') {
    return 'Unknown'
  }
  const parts = userLocation.locationString.split(',')
  // Return only city and country
  if (parts.length > 2) {
    return `${parts.at(0)}, ${parts.at(-1)}`
  }
  return userLocation.locationString
}
